import { makeStyles } from '@material-ui/styles'
import React, { useCallback, useState } from 'react'
import ActionRequiredModalTrigger, {
  ActionRequiredIssue,
} from 'core/components/ActionRequiredModalTrigger'
import ConfirmationDialog from 'core/components/ConfirmationDialog'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import Button from 'core/elements/button'
import Text from 'core/elements/Text'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { copyImage } from './actions'

export interface SyncImage {
  id: string
  name?: string
}

interface Props {
  open: boolean
  images: SyncImage[]
  onClose: () => void
  // Confirm-state heading and body. Default to the blueprint "a new store was added" wording so the
  // blueprint call site is unchanged; the image list passes its own copy.
  title?: string
  prompt?: string
  // Image-library health, shown as the same warning banners as the Add Image modal so the user is
  // told up front when the Glance service is degraded or unreachable before they trigger a copy.
  actionRequiredIssues?: ActionRequiredIssue[]
  glanceHostNotReachable?: boolean
  imageLibraryPresent?: boolean
}

const useStyles = makeStyles<Theme>((theme) => ({
  // Flex column so the gap between the warning and the prompt is exactly 8px, instead of the
  // warning's own margin stacking with the text's intrinsic line spacing.
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  warning: {
    color: theme.components.badge.error.color,
    cursor: 'pointer',

    '& > span': {
      color: theme.components.badge.error.color,
    },
  },
  icon: {
    color: theme.components.badge.error.color,
    marginRight: 8,
  },
}))

// Server-side fan-out body: copy each image to every configured store it is not already in. Glance
// returns 202 (the copy starts in the background, it is not finished synchronously). (D1)
const copyImageBody = {
  method: { name: 'copy-image' },
  all_stores: true,
  all_stores_must_succeed: true,
}

// Confirms, then fires copy-image per image, tracks progress, and lists any images whose copy could
// not be started so the user can retry them later. Shared by the blueprint sync-on-store-add prompt
// and the image list "Copy to all stores" action.
export default function SyncImagesToStoresDialog({
  open,
  images,
  onClose,
  title,
  prompt,
  actionRequiredIssues = [],
  glanceHostNotReachable = false,
  imageLibraryPresent = false,
}: Props) {
  const classes = useStyles()
  // bulkUpdate fans the copies out in parallel (Promise.all over the action) so one image's copy
  // never blocks another's — unlike `update`, which serializes concurrent calls.
  const { bulkUpdate: copyImages } = useUpdateAction(copyImage)
  const [syncing, setSyncing] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [failures, setFailures] = useState<SyncImage[]>([])

  const total = images.length

  // "Action Required" link (same as the Add Image modal) shown when the Glance host is unreachable
  // or needs setup, so the user is warned before starting a copy that may not succeed.
  const healthWarnings =
    imageLibraryPresent && actionRequiredIssues.length ? (
      <ActionRequiredModalTrigger
        issues={actionRequiredIssues}
        modalTitle="Action Required"
        modalIntroText="The following actions are required to access the Image Library Endpoint:"
        trigger={({ openWithEvent }) => (
          <Text variant="subtitle2" className={classes.warning} onClick={openWithEvent}>
            <FontAwesomeIcon className={classes.icon}>info-circle</FontAwesomeIcon>
            {glanceHostNotReachable
              ? 'Image Library Host Not Reachable - Action Required'
              : 'Action Required'}{' '}
            ({actionRequiredIssues.length})
          </Text>
        )}
      />
    ) : null

  const handleConfirm = useCallback(async () => {
    setSyncing(true)
    // Each image's copy is independent, so fire them all at once and wait for every result. Copy is
    // best-effort — bulkUpdate never rejects: it reports each item as { success, params } — so one
    // image failing to start does not stop the others. We list the failures by matching params.id
    // back to the image so the user can retry them later.
    const results = await copyImages(images.map((image) => ({ id: image.id, body: copyImageBody })))
    const failedIds = new Set(results.filter((result) => !result.success).map((r) => r.params.id))
    setFailures(images.filter((image) => failedIds.has(image.id)))
    setSyncing(false)
    setCompleted(true)
  }, [images, copyImages])

  if (completed) {
    return (
      <ConfirmationDialog
        open={open}
        title="Image copy started"
        text={
          failures.length ? (
            <div>
              <Text variant="body1">
                Copy started for {total - failures.length} of {total} images. The copy could not be
                started for the following — you can retry them later:
              </Text>
              <ul>
                {failures.map((image) => (
                  <li key={image.id} data-testid={`sync-image-failure-${image.id}`}>
                    {image.name || image.id}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            `Copy started for all ${total} image(s). Copying runs in the background.`
          )
        }
        customFooterActions={
          <Button variant="primary" onClick={onClose}>
            Close
          </Button>
        }
        onCancel={onClose}
      />
    )
  }

  const defaultPrompt =
    `A new store was added. Copy the ${total} existing image(s) to all stores so they are ` +
    `available everywhere? Copying runs in the background.`

  return (
    <ConfirmationDialog
      open={open}
      // Widened so the "Image Library Host Not Reachable - Action Required" warning fits on one line.
      maxWidth={500}
      title={title ?? 'Copy existing images to all stores?'}
      text={
        <div className={classes.body}>
          {healthWarnings}
          <Text variant="body2">
            {syncing ? `Copying ${total} image(s) to all stores…` : (prompt ?? defaultPrompt)}
          </Text>
        </div>
      }
      confirmText="Copy Images"
      cancelText="Not Now"
      loading={syncing}
      disableConfirmButton={syncing}
      onConfirm={handleConfirm}
      onCancel={onClose}
    />
  )
}
